import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import {
  AlertDialog,
  AlertDialogPrimaryAction,
  AlertDialogSecondaryAction,
  Text,
  TextField,
  theme,
} from '@frameio/vapor';
import { IconCheckmark } from '@frameio/vapor-icons';
import TEXT from './generated/TEXT/DisableMountedStorageDialog';

const CHECKMARK_SIZE = 16;

TEXT.DEFINE({
  /** Details for the confirmation dialog when switching to standard storage */
  details:
    'This project will switch to Standard Storage. Members who currently have it mounted can continue until they unmount, but no new mounts will be allowed.',
  /** Label for the cancel button in the confirmation dialog */
  cancel: 'Cancel',

  /** Instruction prompting the user to type the confirmation text to proceed */
  confirmationInstructions: (confirmationWord: string) =>
    `Type "${confirmationWord.toLowerCase()}" to confirm`,
});

type ConfirmationFormProps = {
  confirmation: string;
  confirmationWord: string;
  defaultDetails?: React.ReactNode;
  details?: React.ReactNode;
  isConfirmed: boolean;
  onConfirmationChange: (value: string) => void;
  onSubmit: (event: React.FormEvent) => void;
};

const ConfirmationForm = ({
  confirmation,
  confirmationWord,
  defaultDetails,
  details,
  isConfirmed,
  onConfirmationChange,
  onSubmit,
}: ConfirmationFormProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  return (
    <form onSubmit={onSubmit}>
      {details && (
        <>
          <Text variant="body">{details}</Text>
          <br />
        </>
      )}
      <Text variant="body">{defaultDetails || TEXT.details}</Text>
      <InputContainer>
        <TextField
          ref={inputRef}
          adornmentAfter={
            <>
              {isConfirmed && (
                <IconCheckmark
                  color={theme.colors.icon.interactive.success}
                  size={CHECKMARK_SIZE}
                />
              )}
            </>
          }
          helpText={TEXT.confirmationInstructions(confirmationWord)}
          hideLabel={true}
          label={TEXT.confirmationInstructions(confirmationWord)?.toString()}
          maxLength={confirmationWord.length}
          onChange={(event) => onConfirmationChange(event.target.value)}
          value={confirmation}
        />
      </InputContainer>
    </form>
  );
};

type DisableMountedStorageDialogProps = {
  confirmButtonText: string;
  confirmationWord: string;
  defaultDetails?: React.ReactNode;
  details?: React.ReactNode;
  isShowing: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  title: React.ReactNode;
};

export const DisableMountedStorageDialog = ({
  confirmButtonText,
  confirmationWord,
  defaultDetails,
  details,
  isShowing,
  onCancel,
  onConfirm,
  title,
}: DisableMountedStorageDialogProps) => {
  const [confirmation, setConfirmation] = useState('');

  const isConfirmed =
    confirmation.toLowerCase() === confirmationWord.toLowerCase();

  const handleConfirm = () => {
    setConfirmation('');
    onConfirm();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (isConfirmed) handleConfirm();
  };

  const handleSubmitKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && isConfirmed) {
      event.preventDefault();
      handleConfirm();
    }
  };

  const handleCancel = () => {
    setConfirmation('');
    onCancel();
  };

  return (
    <AlertDialog
      actions={
        <>
          <AlertDialogSecondaryAction>{TEXT.cancel}</AlertDialogSecondaryAction>
          <AlertDialogPrimaryAction
            isDisabled={!isConfirmed}
            onClick={handleSubmit}
            onKeyDown={handleSubmitKeyDown}
            type="submit"
            variant="destructive"
          >
            {confirmButtonText}
          </AlertDialogPrimaryAction>
        </>
      }
      details={
        <ConfirmationForm
          confirmation={confirmation}
          confirmationWord={confirmationWord}
          defaultDetails={defaultDetails}
          details={details}
          isConfirmed={isConfirmed}
          onConfirmationChange={setConfirmation}
          onSubmit={handleSubmit}
        />
      }
      isShowing={isShowing}
      onRequestClose={handleCancel}
      title={title}
      variant="notice"
    />
  );
};

const InputContainer = styled.div`
  margin: ${theme.spaces[12]} 0;
  text-align: start;
`;
